import { useMemo } from 'react';
import {
  Box,
  Chip,
  LinearProgress,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { SystemJob } from 'src/Api/exchanges';

interface JobProgressStepperProps {
  job: SystemJob;
  /**
   * Noun shown after "Starting"/"Stopping" — e.g. "Unwinder", "Orderbooks",
   * "Everything".
   */
  label: string;
}

/**
 * Shared step-by-step progress view for an on/off `SystemJob`: a header with a
 * completion icon, a determinate progress bar while running, the vertical MUI
 * stepper, and the job-level error. Renders bare content (no Paper, no
 * positioning) so callers can drop it into a popover or a Dialog.
 *
 * Extracted from StopStartUnwinderButtons + ToggleUpdatesButton (which had
 * identical inline copies) and reused by the master StopAllButton.
 */
export const JobProgressStepper = ({ job, label }: JobProgressStepperProps) => {
  const progress = useMemo(() => {
    if (!job.steps.length) return 0;
    const completed = job.steps.filter(s => s.status === 'completed').length;
    return Math.round((completed / job.steps.length) * 100);
  }, [job]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 0.5
        }}
      >
        <Typography
          variant="caption"
          fontWeight={600}
          sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
        >
          {job.type === 'start' ? 'Starting' : 'Stopping'} {label}
        </Typography>
        {job.status === 'completed' && (
          <CheckCircleOutlineIcon
            sx={{ fontSize: 16, color: 'success.main' }}
          />
        )}
        {job.status === 'failed' && (
          <ErrorOutlineIcon sx={{ fontSize: 16, color: 'error.main' }} />
        )}
      </Box>

      {job.status === 'running' && (
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ mb: 1, borderRadius: 1, height: 3 }}
        />
      )}

      <Stepper
        activeStep={job.currentStep}
        orientation="vertical"
        sx={{
          '& .MuiStepConnector-line': { minHeight: 8 },
          '& .MuiStepLabel-label': { fontSize: '0.7rem' },
          '& .MuiStepLabel-iconContainer': { pr: 1 },
          '& .MuiSvgIcon-root': { fontSize: '0.9rem' }
        }}
      >
        {job.steps.map((step, idx) => (
          <Step key={idx} completed={step.status === 'completed'}>
            <StepLabel error={step.status === 'failed'} sx={{ py: 0 }}>
              {step.label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {job.error && (
        <Typography
          variant="caption"
          color="error"
          sx={{ mt: 0.5, display: 'block' }}
        >
          {job.error}
        </Typography>
      )}
    </>
  );
};

/**
 * Result-snackbar model derived from a completed/failed STOP|START|PANIC job.
 * Carries the enriched order-cancel counts the control plane now returns so the
 * snackbar can show "X cancelled, Y still resting" and flag an incomplete cancel.
 */
export interface JobResultSnack {
  kind: 'success' | 'error';
  /** e.g. "Opener stop completed (panic was triggered)." */
  headline: string;
  triggeredPanic: boolean;
  kalshiCancelled?: number;
  polymarketCancelled?: number;
  kalshiRemaining?: number | null;
  polymarketRemaining?: number | null;
  podForceDeleted?: boolean;
  /**
   * Orders still resting after the cancel sweep: a total when every venue
   * answered, `null` when at least one could NOT be confirmed (UNKNOWN).
   *
   * `null` is NOT zero. Summing it away as 0 turns the one outcome that needs
   * the operator — a book nobody could read — into a green "completed", which is
   * how a whole account's resting orders once survived a panic unnoticed.
   */
  remaining: number | null;
  /**
   * The cancel did not demonstrably end with an empty book — orders left, or a
   * venue that could not be confirmed. Callers gate the sticky snackbar on this
   * rather than on `remaining`, whose falsy values (`0` and `null`) mean
   * opposite things.
   */
  cancelUnresolved: boolean;
}

const POLYMARKET_PROXY_CAVEAT =
  'Las órdenes de la UI / proxy-wallet de Polymarket NO se cancelan por panic.';

/**
 * Build the snackbar model from a finished `SystemJob`. `label` is the
 * human noun for the subsystem ("Opener" / "Unwinder"). Shared by both
 * StopStart{Opener,Unwinder}Buttons so the two stay mirror-identical.
 */
export const buildJobResultSnack = (
  label: string,
  job: SystemJob
): JobResultSnack => {
  const verb = job.type === 'stop' ? 'stop' : 'start';
  const triggeredPanic = Boolean(job.triggeredPanic);

  // The counters are read BEFORE branching on status, because a job that could
  // not confirm its cancel is precisely a job that FAILED.
  //
  // The control plane sets `job.*Remaining` and only then checks
  // `verified = replicasZero && kalshiVerified && polymarketVerified`; each
  // venue's `verified` is written next to its `remaining` as `remaining === 0`,
  // so an unconfirmed or non-empty book forces `finishJob(job, false)` and the
  // job arrives here as `failed`, carrying its counters. A `completed` job
  // therefore always reads 0/0 — reporting the cancel only on that branch would
  // be reporting it on the one branch that never has anything to say.
  //
  // Three outcomes, not two. `undefined` = this venue is absent from the
  // payload; `null` = a sweep ran and could not be confirmed; a number = a real
  // count. Only the last one may be added up.
  //
  // An absent venue is UNKNOWN too — but only once SOME venue reported. All of
  // them absent is the job that ran no cancel sweep at all (a START), which has
  // nothing to be unsure about; one absent next to one that answered is a venue
  // nobody has a number for, which is the same thing `null` says. The root's
  // `applyStopCounters` merges venue by venue, so that half-filled shape is
  // reachable, and this is how `describeCancelLeftovers` already reads it on the
  // notifications feed (`v == null`, null or undefined alike).
  const venues = [job.kalshiRemaining, job.polymarketRemaining];
  const reported = venues.filter((v) => v !== undefined);
  const unconfirmed =
    reported.length > 0 &&
    (reported.length < venues.length || reported.some((v) => v === null));
  const remaining = unconfirmed
    ? null
    : venues.reduce<number>((total, v) => total + (v ?? 0), 0);
  const cancelUnresolved = remaining === null || remaining > 0;
  const counters = {
    kalshiCancelled: job.kalshiCancelled,
    polymarketCancelled: job.polymarketCancelled,
    kalshiRemaining: job.kalshiRemaining,
    polymarketRemaining: job.polymarketRemaining,
    podForceDeleted: job.podForceDeleted
  };

  if (job.status === 'failed') {
    return {
      kind: 'error',
      headline: `${label} ${verb} failed: ${job.error ?? 'unknown error'}`,
      triggeredPanic,
      ...counters,
      remaining,
      cancelUnresolved
    };
  }

  // No special wording for an unconfirmed cancel here: a job that could not
  // confirm its sweep never reaches this branch (see above), and the reachable
  // one already carries the reason in `job.error`. The parts that DO cross both
  // branches — the red `kind`, the NOT CONFIRMED banner, the sticky snackbar —
  // are what actually stop the operator, and they need no help from the
  // headline. A branch whose only exercise is a test fixture is not a safety
  // net; it is a claim nobody can check.
  return {
    kind: cancelUnresolved ? 'error' : 'success',
    headline: `${label} ${verb} completed${
      triggeredPanic ? ' (panic was triggered)' : ''
    }.`,
    triggeredPanic,
    ...counters,
    remaining,
    cancelUnresolved
  };
};


/**
 * `null` is UNKNOWN and never 0 — mirrors `describeCancelLeftovers` on the
 * notifications feed. `undefined` renders the same way on purpose: once a job is
 * showing cancel counts at all, a venue missing from that payload is a venue
 * nobody has an answer for, which is exactly what UNKNOWN says.
 */
const showRemaining = (v: number | null | undefined): string =>
  v == null ? 'UNKNOWN' : String(v);

/**
 * Snackbar body for a finished STOP/PANIC job: the headline plus the enriched
 * cancel counts, a prominent warning when orders are still resting, and the
 * Polymarket proxy-wallet caveat whenever a cancel sweep ran.
 */
export const JobResultContent = ({ snack }: { snack: JobResultSnack }) => {
  const hasCounts =
    snack.kalshiCancelled !== undefined ||
    snack.polymarketCancelled !== undefined ||
    snack.kalshiRemaining !== undefined ||
    snack.polymarketRemaining !== undefined;

  return (
    <Box>
      <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
        {snack.headline}
      </Typography>

      {hasCounts && (
        <Stack
          direction="row"
          spacing={0.5}
          flexWrap="wrap"
          useFlexGap
          sx={{ mt: 0.5 }}
        >
          <Chip
            size="small"
            color="default"
            variant="outlined"
            label={`Kalshi: ${showRemaining(snack.kalshiCancelled)} cancelled`}
            sx={{ fontSize: '0.62rem', height: 18 }}
          />
          <Chip
            size="small"
            color="default"
            variant="outlined"
            label={`Polymarket: ${showRemaining(
              snack.polymarketCancelled
            )} cancelled`}
            sx={{ fontSize: '0.62rem', height: 18 }}
          />
        </Stack>
      )}

      {snack.cancelUnresolved && (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{ mt: 0.5 }}
        >
          <WarningAmberIcon sx={{ fontSize: 16, color: 'error.main' }} />
          <Typography
            variant="caption"
            color="error"
            sx={{ fontWeight: 700 }}
          >
            {snack.remaining === null
              ? 'CANCEL NOT CONFIRMED — last read: Kalshi '
              : 'CANCEL INCOMPLETE — still resting: Kalshi '}
            {showRemaining(snack.kalshiRemaining)}, Polymarket{' '}
            {showRemaining(snack.polymarketRemaining)}
          </Typography>
        </Stack>
      )}

      {snack.podForceDeleted && (
        <Typography
          variant="caption"
          sx={{ mt: 0.5, display: 'block', color: 'warning.main' }}
        >
          Engine pod was force-deleted before scaling to 0.
        </Typography>
      )}

      {(hasCounts || snack.triggeredPanic) && (
        <Typography
          variant="caption"
          sx={{
            mt: 0.5,
            display: 'block',
            fontStyle: 'italic',
            opacity: 0.85
          }}
        >
          {POLYMARKET_PROXY_CAVEAT}
        </Typography>
      )}
    </Box>
  );
};
